import React, { useEffect, useState } from 'react';
import {
  fetchUserRoleSelectionForUser,
  deleteUserRoleMappingByUserAndRole,
  createUserRoleMapping,
  UserRoleSelectionProjection
} from './UserRoleMappingApi';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faSpinner, faCheck, faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';

const MODULE_NAME = "UserRoleMapping";

const UserRoleMappingList: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { userId } = useParams();
  const [roles, setRoles] = useState<UserRoleSelectionProjection[]>([]);
  const [loading, setLoading] = useState(false);
  const [updatingRoleId, setUpdatingRoleId] = useState<string | null>(null);

  // Permissions
  const modulePermission = user?.permissions?.find(
    (perm) => perm.moduleName.toLowerCase() === MODULE_NAME.toLowerCase()
  );

  const canView = modulePermission?.canView ?? false;
  const canCreate = modulePermission?.canCreate ?? false;
  const canEdit = modulePermission?.canEdit ?? false;
  const canDelete = modulePermission?.canDelete ?? false;

  useEffect(() => {
    if (userId && canView) loadRoleSelections(userId);
  }, [userId]);

  const loadRoleSelections = async (userId: string) => {
    setLoading(true);
    try {
      const res = await fetchUserRoleSelectionForUser(userId);
      setRoles(res.data);
    } catch (err) {
      console.error('Failed to fetch role selection for user:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCheckboxChange = async (roleId: string, isChecked: boolean) => {
    if (!userId || !canEdit) return;

    setUpdatingRoleId(roleId);
    try {
      if (isChecked) {
        await createUserRoleMapping({ userId, roleId });
      } else {
        await deleteUserRoleMappingByUserAndRole(userId, roleId);
      }
      await loadRoleSelections(userId);
    } catch (err) {
      console.error('Failed to update role mapping:', err);
      alert('Error updating role mapping');
    } finally {
      setUpdatingRoleId(null);
    }
  };

  const handleGrantAccess = async (roleId: string) => {
    if (!userId || !canCreate) return;
    setUpdatingRoleId(roleId);
    try {
      await createUserRoleMapping({ userId, roleId });
      await loadRoleSelections(userId);
    } catch (err) {
      console.error('Failed to grant role mapping:', err);
      alert('Error granting role access');
    } finally {
      setUpdatingRoleId(null);
    }
  };

  const handleDelete = async (roleId: string) => {
    if (!userId || !canDelete) return;
    const confirm = window.confirm('Are you sure you want to remove this role from user?');
    if (!confirm) return;

    setUpdatingRoleId(roleId);
    try {
      await deleteUserRoleMappingByUserAndRole(userId, roleId);
      await loadRoleSelections(userId);
    } catch (err) {
      console.error('Failed to remove role mapping:', err);
      alert('Failed to delete role mapping');
    } finally {
      setUpdatingRoleId(null);
    }
  };

  return (
    <div className="p-6 max-w-5xl mx-auto bg-white rounded shadow">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold">User Role Mapping</h2>
        <button
          onClick={() => navigate(-1)}
          className="bg-gray-200 hover:bg-gray-300 text-sm px-3 py-1 rounded"
        >
          <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
          Back
        </button>
      </div>

      {!canView ? (
        <p className="text-red-600 font-semibold">You do not have permission to view this page.</p>
      ) : loading ? (
        <div className="text-center py-6">
          <FontAwesomeIcon icon={faSpinner} spin className="text-gray-500 text-2xl" />
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full border text-sm table-auto">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-3 py-2 border">#</th>
                <th className="px-3 py-2 border">Role Name</th>
                <th className="px-3 py-2 border">Mapped</th>
                <th className="px-3 py-2 border">Assign</th>
                <th className="px-3 py-2 border">Actions</th>
              </tr>
            </thead>
            <tbody>
              {roles.length === 0 ? (
                <tr>
                  <td colSpan={5} className="text-center py-4 text-gray-500">
                    No roles found.
                  </td>
                </tr>
              ) : (
                roles.map((role, idx) => {
                  const isMapped = !!role.userId;
                  return (
                    <tr key={role.roleId} className="hover:bg-gray-50">
                      <td className="px-3 py-2 border">{idx + 1}</td>
                      <td className="px-3 py-2 border text-blue-600 underline">
                        <Link to={`/admin/roles/${role.roleId}/view`} target="_blank" rel="noopener noreferrer">
                          {role.roleName}
                        </Link>
                      </td>
                      <td className="px-3 py-2 border">
                        {isMapped ? (
                          <span className="text-green-600 font-medium">Yes</span>
                        ) : (
                          <span className="text-gray-400">No</span>
                        )}
                      </td>
                      <td className="px-3 py-2 border text-center">
                        <input
                          type="checkbox"
                          checked={isMapped}
                          onChange={(e) => handleCheckboxChange(role.roleId, e.target.checked)}
                          disabled={updatingRoleId === role.roleId || !canEdit}
                        />
                      </td>
                      <td className="px-3 py-2 border flex gap-2">
                        {!isMapped ? (
                          <button
                            onClick={() => handleGrantAccess(role.roleId)}
                            className="bg-green-600 text-white px-2 py-1 rounded disabled:opacity-50"
                            disabled={updatingRoleId === role.roleId || !canCreate}
                          >
                            <FontAwesomeIcon icon={faCheck} /> Grant Access
                          </button>
                        ) : (
                          <button
                            onClick={() => handleDelete(role.roleId)}
                            className="text-red-600 hover:text-red-800 disabled:opacity-50"
                            title="Unmap Role"
                            disabled={updatingRoleId === role.roleId || !canDelete}
                          >
                            <FontAwesomeIcon icon={faTrash} /> Remove
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default UserRoleMappingList;